import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { useCallback, useEffect, useState } from 'react';
import { InfoWrapper } from '../AISCard';
import { Icon } from '../Icon';
import { AreaCardData, AreaInfo } from 'ais-facade-api/api';
import { ReactComponent as SpinnerLight } from '../../assets/icons/icon-spinner-light.svg';
import { useDeleteAreaMutation, useLazyGetAreasQuery } from 'app/api/marine-infrastructure';
import { Modal } from '../Modal';
import { DeleteAreaConfirmModal } from './DeleteAreaConfirmModal';
import { throttle } from 'app/helpers';
import { useAlertHook } from 'app/hooks/useAlertHook';
import { setSelectedArea } from 'app/utils/marineInfrastructureReducer';
import { AreaForm } from '../AreaForm';
import { VesselsCount } from '../VesselsCount/VesselsCount';

import styles from './AreaCard.module.scss';

interface Props {
  data?: AreaCardData | null;
  isLoading?: boolean;
  onPositionClick?: () => void;
  onClose: () => void;
  toggleVesselsCatalog: () => void;
}
export const AreaCard = ({ data, isLoading, onPositionClick, onClose, toggleVesselsCatalog }: Props) => {
  const { t } = useTranslation();
  const { addAlert } = useAlertHook();
  const dispatch = useAppDispatch();

  const selected = useAppSelector((state) => state.marineInfrastructureSlice.selectedArea);
  const showCard = useAppSelector((state) => state.marineInfrastructureSlice.showAreaCard);

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showUpdateConfirmModal, setShowUpdateConfirmModal] = useState(false);

  const [show, setShow] = useState<boolean>(true);
  const [deleteArea] = useDeleteAreaMutation();

  const [getAreas] = useLazyGetAreasQuery();
  const debouncedGetAreasHandler = useCallback(throttle(getAreas, 700), []);

  const handleDelete = useCallback(() => {
    if (data?.code)
      deleteArea(data.code)
        .unwrap()
        .then(() => {
          debouncedGetAreasHandler({});
          setShowDeleteConfirmModal(false);
          onClose();
          dispatch(setSelectedArea(null));
          addAlert({
            data: {
              title: 'Success',
              status: 200,
              detail: 'Success',
              messages: [{ description: t('GeofenceRemoved'), type: 'information' }],
            },
          });
        })
        .catch((error) => {
          addAlert(error);
        });
  }, [data?.code]);

  useEffect(() => {
    if (selected && Object.keys(selected)?.length > 0) {
      setShow(true);
    }
    if (
      !selected ||
      Object.keys(selected)?.length === 0
    ) {
      setShow(false);
    }
  }, [selected]);

  if (!show || !selected || !showCard) {
    return null;
  }
  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <div className={styles.name}>{selected.name}</div>

          <div onClick={onClose} className={styles.close}>
            <Icon name="close-16" size="16px" />
          </div>
        </div>
        <div className={styles.sub_header}>
          {selected.type === 'COMMON' && ('subType' in selected ? selected?.meta?.subType : t('CommonAreaTitle'))}
          {selected.type === 'USER' && t('UserAreaTitle')}
        </div>

        <div className={styles.info}>
          {isLoading ? (
            <div className={styles.loader}>
              <SpinnerLight className={styles.spinner} />
              {t('DataIsLoading')}
            </div>
          ) : (
            data?.code && selected?.code && data.code === selected.code && (
              <>
                <InfoWrapper
                  label={`${t('AreasArea')}:`}
                  value={
                    data.areaSquareKilometres && (
                      <span>
                        {data.areaSquareKilometres < 1
                          ? Math.round(data.areaSquareKilometres * 10) / 10
                          : Math.round(data.areaSquareKilometres)}{' '}
                        {t('Km')}
                        <sup>2</sup>
                      </span>
                    )
                  }
                />
                <InfoWrapper
                  label={`${t('Perimeter')}:`}
                  value={
                    data.perimeterKilometres && (
                      <span>
                        {data.perimeterKilometres < 1
                          ? Math.round(data.perimeterKilometres * 10) / 10
                          : Math.round(data.perimeterKilometres)}{' '}
                        {t('Km')}
                      </span>
                    )
                  }
                />
                <InfoWrapper
                  label={`${t('AreasVesselsAmount')}:`}
                  clickable={Boolean(data.mmsiCount && data.mmsiCount > 0)}
                  onClick={toggleVesselsCatalog}
                  value={<VesselsCount mmsiCount={data.mmsiCount} />}
                />

                <InfoWrapper
                  label={`API ID:`}
                  value={
                    <div title={t('ClipboardCopy')} className={styles.copy_id}>
                      <span>{data.code}</span>
                      <Icon name="copy" size="14px" />
                    </div>
                  }
                  borderBottom
                  clickable
                  onClick={() => {
                    data.code &&
                      navigator.clipboard.writeText(data.code).then(() => {
                        addAlert({
                          data: {
                            messages: [{ description: t('ClipboardCopySuccess'), type: 'information' }],
                          },
                        });
                      });
                  }}
                />

                <div className={styles.actions}>
                  <div onClick={onPositionClick} className={styles.actionIconContainer}>
                    <Icon name="aim" size="20px" />
                  </div>

                  {data.type === 'USER' && (
                    <div className={styles.rightActions}>
                      <div
                        onClick={() => {
                          setShowUpdateConfirmModal(true);
                        }}
                        className={styles.actionIconContainer}
                      >
                        <Icon name="edit" size="24px" />
                      </div>

                      <div
                        onClick={() => {
                          setShowDeleteConfirmModal(true);
                        }}
                        className={styles.actionIconContainer}
                      >
                        <Icon name="trash" size="24px" />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )
          )}
        </div>
      </div>

      {data && (
        <>
          <Modal
            isShown={showDeleteConfirmModal}
            onClose={() => {
              setShowDeleteConfirmModal(false);
            }}
            headerTitle={t('GeofenceRemoving')}
            component={
              <DeleteAreaConfirmModal
                onSubmit={handleDelete}
                name={data.name}
                onClose={() => {
                  setShowDeleteConfirmModal(false);
                }}
              />
            }
          />
          <Modal
            isShown={showUpdateConfirmModal}
            onClose={() => {
              setShowUpdateConfirmModal(false);
            }}
            headerTitle="Редактирование"
            component={
              <AreaForm
                mode="EDIT"
                area={data}
                onClose={() => {
                  setShowUpdateConfirmModal(false);
                }}
                onSuccess={(area: AreaInfo) => {
                  debouncedGetAreasHandler({});
                  dispatch(setSelectedArea(area));
                }}
              />
            }
          />
        </>
      )}
    </>
  );
};
